import './spot-difference.css';
import { renderGameMessage, startBlockedMessage } from '../shared/game-message';

const SCRIPT_URL = '/build/games/spot-difference/spot-difference.min.js';

import imgGameIcon from '../../images/game-icon.svg';

export const WELCOME_CONFIG = {
    icon: imgGameIcon,
    title: 'Witaj w Spot the Glow',
    description: '<p>Na ekranie zobaczysz dwa niemal identyczne obrazki, ale ukryto między nimi kilka różnic.</p>' +
        '<p>Twoim zadaniem jest odnalezienie wszystkich różnic przed końcem czasu. Uważaj jednak – błędne kliknięcia mogą kosztować Cię cenne sekundy!</p>',
    howToPlay: {
        heading: 'JAK GRAĆ?',
        body: '<ol>' +
            '<li>Obróć telefon poziomo.</li>' +
            '<li>Porównuj dwa obrazki i klikaj miejsca, w których widzisz różnice.</li>' +
            '<li>Znajdź wszystkie różnice, zanim upłynie czas!</li>' +
            '</ol>',
    },
    scoring: {
        heading: 'PUNKTACJA',
        body: '<ul>' +
            '<li>✅ Poprawnie wskazana różnica zostaje oznaczona na ekranie.</li>' +
            '<li>✅ Każda znaleziona różnica to 3 punkty.</li>' +
            '<li>⭐ Znalezienie wszystkich różnic na poziomie daje dodatkowe 2 punkty.</li>' +
            '<li>⏱️ Liczy się czas – im szybciej znajdziesz wszystkie różnice, tym lepiej.</li>' +
            '<li>❌ Błędne kliknięcie odejmuje czas z timera.</li>' +
            '<li>⌛ Gra kończy się po znalezieniu wszystkich różnic lub po upływie czasu.</li>' +
            '</ul>' +
            '<p>Maksymalny wynik to 44 punkty.<br />Gotowy? Sprawdź, jak szybko wypatrzysz wszystkie różnice! 🔍✨</p>',
    },
};

import imgLevelUp from './images/level-up.svg';
import imgRotatePhone from './images/rotate-phone.svg';

import sndCorrect from './sounds/correct.wav';
import sndWrong from './sounds/wrong.wav';
import sndGameOver from './sounds/gameover.wav';
import sndCountdown from './sounds/wrong.wav';

let gameInstance  = null;
let currentTaskId = null;
let _orientationMql = null;
let _onLandscapeFullscreen = null;

const ASSETS_CONFIG = {
    texts: {
        gameTitle: 'Spot the Glow',
        gameSubtitle: 'Kliknij na różnice między obrazkami!',
        startButton: 'Rozpocznij grę',
        foundLabel: 'Znalezione:',
        mistakesLabel: 'Błędy:',
        errorPrefix: 'Błąd w trakcie ładowania gry:',
        loading: 'Ładowanie...',
        foundToast: 'Znaleziono {found} z {total}!',
        mistakeToast: 'To nie różnica!',
        allFoundToast: 'Wszystkie różnice znalezione!',
        levelCompleteToast: 'Poziom ukończony!',
        timeoutToast: 'Czas minął!',
        levelModalTitle: 'Idzie Ci świetnie!',
        levelModalText: 'Przygotuj się na trudniejsze obrazki do porównania ;)',
        levelModalButton: 'Następny poziom',
        resultTitle: 'Wynik',
        resultTitleTimeout: 'Czas minął!',
        resultDifferences: 'Różnice',
        resultMistakes: 'Błędy',
        resultRankingPoints: 'Punkty rankingowe',
        resultRedeemablePoints: 'Punkty do wydania',
        playAgain: 'Zagraj ponownie',
        rotatePrompt: 'Obróć urządzenie poziomo, aby grać',
    },
    images: {background: null, logo: null, resultIcon: null, rotateIcon: imgRotatePhone, levelModalImage: imgLevelUp},
    sounds: {correct: sndCorrect, wrong: sndWrong, gameOver: sndGameOver, countdown: sndWrong},
    colors: {
        primary: '#0d6efd',
        success: '#4cff91',
        danger: '#ff4f4f',
        dark: '#111111',
        background: 'linear-gradient(180deg, #FF66CC 1.79%, #FF4E31 50.7%, #FFAF45 97.7%),' +
            'linear-gradient(333.62deg, #FF66CC 0%, #FF4E31 38.66%, #FFAF45 75.8%)',
        surface: 'rgba(255, 255, 240, 1)',
        text: '#ffffff',
        textMuted: '#aaaaaa',
        markerFound: '#4cff91',
        markerMiss: '#ff3c3c',
        btnGradient: 'linear-gradient(160deg, #FFAF45 9.47%, #FF4E31 49.5%, #FF66CC 89.54%)',
    },
};

function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
    });
}

function contentEl(modal) {
    return modal.querySelector('.game-modal__content');
}

// Mobile browser only — exclude desktop (fine-pointer) and installed PWAs,
// where element fullscreen is either unnecessary or unsupported.
function isMobileBrowser() {
    const isMobile = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        || window.matchMedia('(display-mode: fullscreen)').matches
        || window.navigator.standalone === true;
    return isMobile && !isStandalone;
}

function requestGameFullscreen(el) {
    if (!el || document.fullscreenElement) return;
    const req = el.requestFullscreen ?? el.webkitRequestFullscreen;
    if (typeof req !== 'function') return;
    try {
        const p = req.call(el);
        // Browsers reject when not granted (no user activation, iOS, etc.) — ignore.
        if (p && typeof p.catch === 'function') p.catch(() => { /* not allowed */ });
    } catch { /* ignore */ }
}

function exitGameFullscreen() {
    if (!document.fullscreenElement) return;
    try {
        const p = (document.exitFullscreen ?? document.webkitExitFullscreen)?.call(document);
        if (p && typeof p.catch === 'function') p.catch(() => { /* ignore */ });
    } catch { /* ignore */ }
}

// After the player rotates to landscape, try to enter fullscreen so the game
// uses the whole viewport. Only on mobile browsers (not PWA). The rotation gives
// the browser the user-activation context most engines require for fullscreen.
// Rotating back to portrait exits fullscreen again.
function attachLandscapeFullscreen(container) {
    if (!isMobileBrowser()) return;
    _onLandscapeFullscreen = () => {
        if (window.matchMedia('(orientation: landscape)').matches) {
            requestGameFullscreen(container);
        } else {
            exitGameFullscreen();
        }
    };
    _orientationMql = window.matchMedia('(orientation: landscape)');
    _orientationMql.addEventListener?.('change', _onLandscapeFullscreen);
    window.addEventListener('orientationchange', _onLandscapeFullscreen);
    // Handle the case where the device is already in landscape on open.
    _onLandscapeFullscreen();
}

function detachLandscapeFullscreen() {
    if (_onLandscapeFullscreen) {
        _orientationMql?.removeEventListener?.('change', _onLandscapeFullscreen);
        window.removeEventListener('orientationchange', _onLandscapeFullscreen);
        _onLandscapeFullscreen = null;
        _orientationMql = null;
    }
    exitGameFullscreen();
}

export function renderSpotDifference(modal, data, markCardDone) {
    const taskId = data.task.id;
    currentTaskId = taskId;
    const cfg = data.spotDifferenceGame.config;

    const el = contentEl(modal);
    el.innerHTML = '';

    const containerId = 'sd-game-container';
    const container = document.createElement('div');
    container.id = containerId;
    container.className = 'sd-game';
    el.appendChild(container);

    loadScript(SCRIPT_URL)
        .then(() => {
            if (typeof SpotDifferenceGame === 'undefined') {
                console.error('SpotDifferenceGame: script loaded but SpotDifferenceGame is not defined');
                return;
            }

            gameInstance = SpotDifferenceGame.init(containerId, {
                assetsConfig: ASSETS_CONFIG,
                gameConfig: {
                    timeLimitSeconds: cfg.timeLimitSeconds ?? 90,
                    mistakePenaltySeconds: cfg.mistakePenaltySeconds ?? 5,
                    timerWarnSeconds: cfg.timerWarnSeconds ?? 10,
                    autoStart: true,
                },
                apiConfig: {
                    useMock: false,
                    endpoints: {
                        startGame:    `/api/task/${taskId}/spot-difference/start`,
                        checkSpot:    `/api/task/${taskId}/spot-difference/check`,
                        submitResult: `/api/task/${taskId}/spot-difference/submit`,
                    },
                },
                callbacks: {
                    onError: (error) => {
                        if (error?.status === 409 && (error.data?.error === 'already_completed' || error.data?.error === 'game_in_progress')) {
                            renderGameMessage(modal, startBlockedMessage(error.data?.error));
                            return;
                        }
                        document.dispatchEvent(new CustomEvent('game-modal:request-close'));
                        alert(`Błąd gry: ${error}`);
                    },
                    onComplete: (result) => {
                        document.body.classList.remove('has-landscape-game');

                        markCardDone({rankingPointsEarned: result.rankingPointsEarned ?? 0, redeemablePointsEarned: result.redeemablePointsEarned ?? 0, checkIn: result.checkIn});
                        document.dispatchEvent(new CustomEvent('game-modal:result-show', {
                            detail: {
                                rankingPoints: result.rankingPointsEarned ?? 0,
                                redeemablePoints: result.redeemablePointsEarned ?? 0,
                            },
                        }));
                    },
                }
            });

            // First interaction (first check fired): lock the game server-side
            // (NEW -> STARTED, records the daily check-in). A 409 means the play was
            // already consumed elsewhere — stop the running game and show the message.
            gameInstance.eventBus.on('game:lock', () => {
                const sessionToken = gameInstance?.sessionToken;
                if (!sessionToken) return;
                fetch(`/api/task/${taskId}/spot-difference/begin`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({sessionToken}),
                })
                    .then(async (res) => {
                        if (res.status !== 409) return;
                        const data = await res.json().catch(() => ({}));
                        if (gameInstance) {
                            try { gameInstance.destroy(); } catch { /* ignore */ }
                            gameInstance = null;
                        }
                        renderGameMessage(modal, startBlockedMessage(data?.error));
                    })
                    .catch(() => { /* fire-and-forget; submit locks defensively as a fallback */ });
            });

            attachLandscapeFullscreen(container);
        })
        .catch(() => console.error('SpotDifferenceGame: failed to load game script'));
}

export async function forceEndSpotDifference() {
    if (gameInstance) {
        try {
            if (currentTaskId !== null) {
                await fetch(`/api/task/${currentTaskId}/spot-difference/flag-force-end`, { method: 'POST' });
            }
            gameInstance.forceEnd();
        } catch { /* ignore */ }
    }
}

export function teardownSpotDifference(modal) {
    detachLandscapeFullscreen();
    if (gameInstance) {
        try {
            gameInstance.destroy();
        } catch { /* ignore */
        }
        gameInstance = null;
    }
    currentTaskId = null;
    const el = contentEl(modal);
    if (el) el.innerHTML = '';
}
